/**
 * Frontend mirror of the backend's Character/Health/Inventory/Equipment sheet
 * contracts (backend/src/game/*\/*.types.ts). No shared types package exists
 * between frontend and backend (separate tsconfigs) — this repo's existing
 * pattern is to re-declare the shape client-side (see core/state/auth-store.ts's
 * own AuthUser). Keep in sync with the backend types by hand.
 */

export interface CharacterAttributes {
  strength: number;
  intellect: number;
  stamina: number;
  agility: number;
  perception: number;
}

/**
 * The wire codes `POST /character/allocate` accepts — the backend's own
 * `AttributeCode` (`character.types.ts`'s `ATTRIBUTE_CODE_TO_FIELD` keys),
 * which is a different vocabulary from the `CharacterAttributes` field names
 * above. `ATTRIBUTE_ROWS` in `ui/paperdoll.ts` is what pairs the two, and is
 * the only place that mapping should live.
 */
export type AttributeCode = 'STR' | 'INT' | 'STA' | 'AGI' | 'PER';

export interface DerivedStats {
  power: number;
  carryWeight: number;
  hpAmount: number;
  hpRegenPerMin: number;
  walkSpeed: number;
  runSpeed: number;
  sprintTime: number;
  reloadSpeed: number;
  aimSpeed: number;
  aimPrecision: number;
  shootSpeed: number;
  energyRegenPerMin: number;
  craftTimeMult: number;
  buildTimeMult: number;
  lootQualityMult: number;
  learnRateMult: number;
}

export interface CharacterSheetActive {
  state: 'ACTIVE';
  presetId: string;
  colorScheme: number;
  level: number;
  /** LIFETIME total, not progress into the current level — see `xpIntoLevel`. */
  xp: number;
  /**
   * Progress into the CURRENT level and what that level costs, both computed
   * server-side (`CharacterService.xpProgress`).
   *
   * Sent rather than derived here because the curve's divisor is an env value
   * (`XP_PER_LEVEL`) the client has no access to, and because `xp` is
   * cumulative — a HUD dividing `xp` by anything it can see locally would be
   * wrong the moment the server runs a non-default value.
   */
  xpIntoLevel: number;
  xpForNextLevel: number;
  /** RAW allocated attributes — buffs are NOT folded in (see `buffs`). */
  attributes: CharacterAttributes;
  /**
   * Live sweets/books buffs; already counted in `stats`. Optional so a sheet
   * from an older server still type-checks as "no buffs".
   */
  buffs?: ActiveStatBuff[];
  attributePoints: number;
  /**
   * The per-attribute ceiling (`CharacterConfig.attrMax`, admin-tunable). The
   * server has sent it since the batch-allocate work and nothing here read it,
   * so the `+` stayed live at the cap and the player discovered the limit by
   * losing a staged set on Save. Never hardcode it — the whole reason it is on
   * the sheet is that it is config, not a constant.
   */
  attrMax: number;
  stats: DerivedStats;
  energy: number;
  maxEnergy: number;
  /** The running energy regen boost, or null. Absent from a server older than 2026-10-19. */
  energyBoost?: { until: string; mult: number } | null;
  raidEnergyCost: number;
  /** Region tier (1 green, 2 yellow, 3 red) -> energy a raid there costs. */
  raidEnergyCostByTier: Record<number, number>;
  poiEnergyCost: number;
  canRaid: boolean;
  canVisitPoi: boolean;
  specializationId: string | null;
  softBalance: number;
  premiumBalance: number;
}

export interface CharacterSheetUncreated {
  state: 'UNCREATED';
}

export type CharacterSheet = CharacterSheetActive | CharacterSheetUncreated;

export interface HealthSheetActive {
  state: 'ACTIVE';
  hp: number;
  maxHP: number;
  hpRegenPerMin: number;
  atMedCenter: boolean;
  downed: boolean;
}

export interface HealthSheetUncreated {
  state: 'UNCREATED';
}

export type HealthSheet = HealthSheetActive | HealthSheetUncreated;

/** Mirrors backend `game/inventory/item-stats.ts` — `ItemDef.statMods`. */
export interface ItemStatMods {
  flat?: Record<string, number>;
  pct?: Record<string, number>;
}

/**
 * Mirrors backend `game/inventory/item-stats.ts`.
 *
 * One shape for "what is this item mechanically", carried by BOTH an owned
 * inventory row and a vendor's stock entry, so `renderItemTooltip` is written
 * once and both surfaces describe an item identically.
 *
 * `weight` here is the item's UNIT weight. `InventoryItemSummary.weight` is
 * the ROW's weight (unit x quantity) — for a 50-round stack the two differ,
 * and conflating them makes the tooltip claim one bullet weighs 2 kg.
 */
export interface ItemStatsSummary {
  rarity: string;
  value: number;
  weight: number;
  slot: string | null;
  armorClass: string | null;
  armorPoints: number | null;
  baseDamage: number | null;
  fixedDamage: number | null;
  ammoType: string | null;
  maxDurability: number;
  statMods: ItemStatMods;
  attrReq: Record<string, number> | null;
  /** What using it does, or null. Optional: an older server omits it. */
  effect?: ConsumableEffect | null;
}

export interface InventoryItemSummary {
  id: string;
  itemId: string;
  name: string;
  kind: string;
  /** This ROW's weight — unit weight x `quantity`. See `stats.weight` for one unit. */
  weight: number;
  acquiredAt: string;
  slot: EquipmentSlot | null;
  iconPath: string | null;
  /** `kind === 'CONSUMABLE'` with a populated effect — offers a "Use" action. */
  usable: boolean;
  /**
   * Units in this row. Always 1 unless the def stacks.
   *
   * The backend has sent this since ammo shipped; this mirror simply never
   * declared it, so the slot grid could not render a stack count and 50 rounds
   * read as one bullet.
   */
  quantity: number;
  /** `ItemDef.maxStack`, or null when the item does not stack. */
  maxStack: number | null;
  stats: ItemStatsSummary;
}

export interface InventorySheet {
  carried: InventoryItemSummary[];
  hideout: InventoryItemSummary[];
  carriedWeight: number;
  carryWeightCap: number;
  carriedSlotCount: number;
  carriedSlotCap: number;
  hideoutSlotCount: number;
  hideoutSlotCap: number;
}

/** Mirrors backend `consumable.types.ts` — the effect kinds `ItemDef.consumableEffect` can carry. */
export interface HpRestoreEffect {
  kind: 'HP_RESTORE';
  amount: number;
}

/** Energy regen x `mult` for a while (absent `mult` = the legacy x2). A stronger boost replaces a weaker one. */
export interface EnergyBoostEffect {
  kind: 'ENERGY_BOOST';
  durationMs: number;
  mult?: number;
}

/** Instant energy — the Gold shop's Energy tab (2026-10-19). */
export interface EnergyRestoreEffect {
  kind: 'ENERGY_RESTORE';
  amount: number;
}

/** HP regen x `mult` for a while — the Gold shop's +25 / +50 / +100% (2026-10-19). */
export interface HpRegenBoostEffect {
  kind: 'HP_REGEN_BOOST';
  mult: number;
  durationMs: number;
}

/** Sweets (+AGI) and books (+INT) — mirrors backend `StatBuffEffect`. */
export interface StatBuffEffect {
  kind: 'STAT_BUFF';
  attribute: keyof CharacterAttributes;
  amount: number;
  durationMs: number;
}

export type ConsumableEffect =
  | HpRestoreEffect | EnergyBoostEffect | EnergyRestoreEffect | HpRegenBoostEffect | StatBuffEffect;

/** A live sweets/books buff on the sheet — mirrors backend `ActiveStatBuff`. */
export interface ActiveStatBuff {
  attribute: keyof CharacterAttributes;
  amount: number;
  /** ISO. */
  until: string;
}

export interface EnergyRefillResult {
  energy: number;
  maxEnergy: number;
  cost: number;
  cash: number;
  gold: number;
}

export interface UseItemResult {
  itemKey: string;
  itemName: string;
  effect: ConsumableEffect;
  /** Populated for HP_RESTORE. */
  hp?: number;
  maxHP?: number;
  /** Populated for ENERGY_BOOST (ISO string over the wire). */
  energyBoostUntil?: string;
  /** Populated for STAT_BUFF — when the (refreshed) buff runs out, ISO. */
  buffUntil?: string;
}

export const EQUIPMENT_SLOTS = ['HEADGEAR', 'TORSO', 'VEST', 'LEGS', 'GLOVES', 'FEET', 'PRIMARY', 'SECONDARY', 'BACKPACK'] as const;
export type EquipmentSlot = (typeof EQUIPMENT_SLOTS)[number];

export interface ItemDefSummary {
  key: string;
  name: string;
  slot: EquipmentSlot | null;
  kind: string;
  rarity: string;
  armorClass: string | null;
  /** ARMOUR only — this piece's contribution to `EquipmentSheet.maxArmor` (weapons-equipment.md F4). */
  armorPoints: number | null;
  iconPath: string | null;
  baseDamage: number | null;
  fixedDamage: number | null;
  ammoType: string | null;
  maxDurability: number;
  /** On the wire from `toDefSummary` (equipment.service.ts); read by the Atelier's stat-delta line. */
  statMods?: { flat?: Record<string, number>; pct?: Record<string, number> };
}

export interface EquippedItemSummary {
  id: string;
  itemId: string;
  def: ItemDefSummary;
  durability: number;
  maxDurability: number;
  broken: boolean;
}

export interface EquipmentSheet {
  slots: Partial<Record<EquipmentSlot, EquippedItemSummary>>;
  statContributions: { flat: Partial<Record<keyof DerivedStats, number>>; pct: Partial<Record<keyof DerivedStats, number>> };
  /** Atelier sets worn whole and unbroken; their bonus is already in `statContributions`. */
  activeSets?: string[];
  blendedStats: DerivedStats;
  /**
   * Σ equipped armour points × durability factor (health-recovery.md F3, minus
   * its still-TBD stat/spec buffs). 0 with no armour equipped — base armour
   * from no gear is 0 by design.
   *
   * There is no *current* armour value to pair this with in the settlement:
   * armour is a per-expedition transient that refills to `maxArmor` on
   * returning (health rule 4), so in the settlement the shield is by
   * definition full. The HUD renders it as such.
   */
  maxArmor: number;
  /**
   * The precision arc the equipped weapon draws — Battleground F1/F2, resolved
   * SERVER-side (`backend/src/game/battleground/aim-arc.ts`) by the very same
   * function the duel room uses to decide what a swing hits.
   *
   * Not derivable here: F2 narrows the firearm cone by `precToArcWidth` and
   * floors it at `minArcWidth`, both admin-tunable. A client-side copy would
   * quietly draw a cone the server had stopped honouring — the reason
   * `GET /vendor/:key/quote` exists, applied to geometry instead of prices.
   *
   * Optional only for the wire: an older backend omits it and `aim-arc.ts`
   * then draws nothing rather than guessing.
   */
  aimArc?: AimArcSummary;
}

/** @see EquipmentSheet.aimArc — mirrors `AimArcSummary` in `equipment.types.ts`. */
export interface AimArcSummary {
  kind: 'MELEE' | 'FIREARM' | 'UNARMED';
  /** Reach / effective range, metres. */
  arcLength: number;
  /** FULL cone angle, DEGREES. */
  arcWidth: number;
  itemKey: string | null;
}
